/**
 * TpsWarp — 2D thin-plate-spline residual warp for the geo→model projection.
 *
 * The hand-modelled terrain GLB is not geo-faithful: a global linear projection has a
 * ~25 m median residual against Alex' hand-placed WPT nodes. This warp interpolates the
 * LOCAL model distortion between known correspondences (anchor = projected position,
 * value = residual vector to the true node position), so points BETWEEN anchors land
 * where the model expects them rather than where the linear math puts them.
 *
 * Solved as two independent TPS problems (dx and dz) with the classic kernel
 * U(r) = r² · ln(r). Anchor counts are small (< 50), so a plain Gauss solver suffices.
 * Corrections are clamped so extrapolation far outside the anchor cloud stays sane.
 */

export interface TpsAnchor { x: number; z: number; dx: number; dz: number; }

export class TpsWarp {
    private anchors: TpsAnchor[];
    private wx: number[] = []; // weights for dx (N rbf + 3 affine)
    private wz: number[] = []; // weights for dz
    private maxCorrection: number;
    public readonly valid: boolean;

    constructor(anchors: TpsAnchor[], maxCorrection = 50) {
        this.anchors = anchors;
        this.maxCorrection = maxCorrection;
        this.valid = anchors.length >= 4 && this.solve();
    }

    private static kernel(r2: number): number {
        if (r2 < 1e-12) return 0;
        return 0.5 * r2 * Math.log(r2); // = r² ln r
    }

    private solve(): boolean {
        const n = this.anchors.length;
        const m = n + 3;
        // Build system matrix [[K, P],[Pᵀ, 0]] once; solve for both target components.
        const A: number[][] = Array.from({ length: m }, () => new Array(m).fill(0));
        for (let i = 0; i < n; i++) {
            for (let j = 0; j < n; j++) {
                const dx = this.anchors[i].x - this.anchors[j].x;
                const dz = this.anchors[i].z - this.anchors[j].z;
                A[i][j] = TpsWarp.kernel(dx * dx + dz * dz);
            }
            A[i][i] += 1e-6; // light regularization
            A[i][n] = 1; A[i][n + 1] = this.anchors[i].x; A[i][n + 2] = this.anchors[i].z;
            A[n][i] = 1; A[n + 1][i] = this.anchors[i].x; A[n + 2][i] = this.anchors[i].z;
        }
        const bx = new Array(m).fill(0);
        const bz = new Array(m).fill(0);
        for (let i = 0; i < n; i++) { bx[i] = this.anchors[i].dx; bz[i] = this.anchors[i].dz; }

        const solX = TpsWarp.gauss(A.map(r => r.slice()), bx.slice());
        const solZ = TpsWarp.gauss(A.map(r => r.slice()), bz.slice());
        if (!solX || !solZ) return false;
        this.wx = solX; this.wz = solZ;
        return true;
    }

    private static gauss(A: number[][], b: number[]): number[] | null {
        const n = b.length;
        for (let col = 0; col < n; col++) {
            let piv = col;
            for (let r = col + 1; r < n; r++) if (Math.abs(A[r][col]) > Math.abs(A[piv][col])) piv = r;
            if (Math.abs(A[piv][col]) < 1e-12) return null;
            [A[col], A[piv]] = [A[piv], A[col]];
            [b[col], b[piv]] = [b[piv], b[col]];
            for (let r = col + 1; r < n; r++) {
                const f = A[r][col] / A[col][col];
                for (let c = col; c < n; c++) A[r][c] -= f * A[col][c];
                b[r] -= f * b[col];
            }
        }
        const x = new Array(n).fill(0);
        for (let r = n - 1; r >= 0; r--) {
            let s = b[r];
            for (let c = r + 1; c < n; c++) s -= A[r][c] * x[c];
            x[r] = s / A[r][r];
        }
        return x;
    }

    /** Residual correction (model units) at a projected position. */
    public correction(x: number, z: number): { dx: number; dz: number } {
        if (!this.valid) return { dx: 0, dz: 0 };
        const n = this.anchors.length;
        let dx = this.wx[n] + this.wx[n + 1] * x + this.wx[n + 2] * z;
        let dz = this.wz[n] + this.wz[n + 1] * x + this.wz[n + 2] * z;
        for (let i = 0; i < n; i++) {
            const ddx = x - this.anchors[i].x;
            const ddz = z - this.anchors[i].z;
            const k = TpsWarp.kernel(ddx * ddx + ddz * ddz);
            dx += this.wx[i] * k;
            dz += this.wz[i] * k;
        }
        // Clamp: far outside the anchor cloud the spline extrapolates — keep it bounded.
        const len = Math.hypot(dx, dz);
        if (len > this.maxCorrection) {
            const f = this.maxCorrection / len;
            dx *= f; dz *= f;
        }
        return { dx, dz };
    }
}
